import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LayoutGrid, Users, Star, HelpCircle, Download, Share2, ChevronRight } from 'lucide-react';
import { useQuery } from '../hooks/useQuery';
import { PokemonCard } from '../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../lib/constants';
import { Features } from '../components/landing/Features';
import { CommunityStats } from '../components/landing/CommunityStats';
import { SEO } from '../components/seo/SEO';
import { useEffect, useState } from 'react';
import 'tailwindcss/tailwind.css';
import { AdSense } from '../components/ads/AdSense';
import { useTranslation } from '../hooks/useTranslation';
import { useI18n } from '../contexts/I18nContext';

export function Landing() {
  const { user } = useAuth();
  const { data: featuredCards } = useQuery<PokemonCard[]>(
    'cards',
    {},
    { withRelations: '*' }
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const { t } = useTranslation();
  const { getPathForLang } = useI18n();

  useEffect(() => {
    // Animation d'entrée progressive
    setIsLoaded(true);
  }, []);

  const displayCards = featuredCards
    ?.filter(card => card.image && ['♛', '☆☆☆'].includes(card.rarity || ''))
    .slice(0, 6) || [];

  return (
    <div className="min-h-screen bg-gray-50 overflow-hidden">
      <SEO 
        title={t('landing.seo.title')}
        description={t('landing.seo.description')}
        og_image={`${SUPABASE_IMAGE_BASE_URL}${displayCards[0]?.image}`}
        og_title={t('landing.seo.og_title')}
        og_description={t('landing.seo.og_description')}
      />
      
      {/* Style pour les animations et les éléments de fond */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .pokeball-bg {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 40%, transparent 70%);
          z-index: 0;
          opacity: 0.6;
        }
        
        .fade-in {
          animation: fadeIn 0.8s ease-out forwards;
        }
        
        .card-hover {
          transition: all 0.3s ease;
          transform-style: preserve-3d;
        }
        
        .card-hover:hover {
          transform: translateY(-10px) rotateY(5deg);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        
        .hero-gradient {
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #6d28d9 100%);
        }
        
        .btn-primary {
          background: linear-gradient(to right, #2563eb, #4f46e5);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-weight: 600;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        
        .btn-secondary {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        
        .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }
      `}} />
      
      {/* Hero Section avec effet de fond amélioré */}
      <section aria-labelledby="hero-heading" className="relative hero-gradient overflow-hidden py-16 sm:py-24">
        {/* Éléments décoratifs de fond */}
        <div className="pokeball-bg absolute top-20 left-[10%] opacity-30"></div>
        <div className="pokeball-bg absolute top-40 right-[15%] opacity-30"></div>
        <div className="pokeball-bg absolute bottom-20 left-[30%] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <div className="text-center mb-8">
              <img
                src="https://image.pokemonpocket.fr/logo.png"
                alt="Logo Pokémon TCG Pocket - Application de gestion de collection"
                className="mx-auto h-24 w-auto mb-6 drop-shadow-lg"
                width="192"
                height="96"
                style={{animation: 'float 6s infinite ease-in-out'}}
              />
              <h1 id="hero-heading" className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-white mb-8 drop-shadow-md">
                <span className="block fade-in" style={{animationDelay: '0.2s'}}>{t('landing.hero.title')}</span>
                <span className="block text-yellow-300 mt-2 fade-in" style={{animationDelay: '0.4s'}}>{t('landing.hero.subtitle')}</span>
              </h1>
            </div>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-white sm:text-2xl fade-in" style={{animationDelay: '0.6s', textShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
              {t('landing.hero.description')}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4 sm:gap-6 fade-in" style={{animationDelay: '0.8s'}}>
              {user ? (
                <Link
                  to={getPathForLang("/sets")}
                  className="btn-primary inline-flex items-center"
                >
                  <LayoutGrid className="w-5 h-5 mr-2" />
                  {t('landing.hero.manage_collection')}
                </Link>
              ) : (
                <>
                  <Link
                    to={getPathForLang("/login")}
                    className="btn-secondary"
                  >
                    {t('landing.hero.login')}
                  </Link>
                  <Link
                    to={getPathForLang("/register")}
                    className="btn-primary inline-flex items-center"
                  >
                    {t('landing.hero.register')}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Annonce AdSense unique */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-neutral-100 max-w-5xl mx-auto">
            <div className="mt-6 mb-8 flex justify-center items-center">
              <AdSense adSlot="1424794127" id="landing-main" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <Features />

      {/* Community Stats Section */}
      <CommunityStats />

      {/* Section des Cartes en Vedette - Design amélioré */}
      {displayCards.length > 0 && (
        <section aria-labelledby="featured-cards-heading" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center mb-12">
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full mb-3 inline-block">{t('landing.featured_cards.label')}</span>
            <h2 id="featured-cards-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('landing.featured_cards.title')}</h2>
            <p className="mt-2 max-w-2xl mx-auto text-lg text-gray-600">
              {t('landing.featured_cards.description')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
            {displayCards.map((card, index) => (
              <div
                key={card.id}
                className="card-hover p-4 bg-white rounded-xl shadow-md relative overflow-hidden group"
                style={{animationDelay: `${0.1 * index}s`}}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <img
                  src={`${SUPABASE_IMAGE_BASE_URL}${card.image}`}
                  alt={`Carte Pokémon ${card.card_fr || 'TCG Pocket'} - ${card.rarity || 'Rare'}`}
                  className="w-full rounded-lg shadow-sm transform transition duration-300 group-hover:scale-105"
                  loading="lazy"
                  width="200"
                  height="280"
                />
                <div className="pt-3 pb-1 relative z-10">
                  <p className="text-sm font-medium text-gray-900 truncate">{card.card_fr || 'Carte Pokémon'}</p>
                  <p className="text-xs font-light text-blue-600">{t('landing.featured_cards.rarity')}{card.rarity || 'Spéciale'}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Section de Prévisualisation - Design amélioré */}
      <section aria-labelledby="about-heading" className="bg-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-50 opacity-50 z-0 pointer-events-none">
          <div className="pokeball-bg absolute top-1/4 left-[20%]"></div>
          <div className="pokeball-bg absolute bottom-1/4 right-[10%]"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-3 py-1 rounded-full mb-3 inline-block">{t('landing.about.label')}</span>
            <h2 id="about-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('landing.about.title')}</h2>
            <p className="mt-2 max-w-2xl mx-auto text-lg text-gray-600">
              {t('landing.about.description')}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texte */}
            <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {t('landing.about.subtitle')}
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                {t('landing.about.subtext')}
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1 bg-blue-100 rounded-lg p-2">
                    <LayoutGrid className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-1">{t('landing.about.feature1_title')}</h4>
                    <p className="text-gray-600">{t('landing.about.feature1_text')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1 bg-indigo-100 rounded-lg p-2">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-1">{t('landing.about.feature2_title')}</h4>
                    <p className="text-gray-600">{t('landing.about.feature2_text')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1 bg-purple-100 rounded-lg p-2">
                    <Star className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-1">{t('landing.about.feature3_title')}</h4>
                    <p className="text-gray-600">{t('landing.about.feature3_text')}</p>
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <Link
                  to={getPathForLang("/register")}
                  className="btn-primary inline-flex items-center"
                >
                  {t('landing.about.join_button')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="lg:col-span-1 flex justify-center items-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl blur-xl opacity-70"></div>
                <img
                  src="https://image.pokemonpocket.fr/preview.png"
                  alt="Interface de gestion de collection Pokémon TCG Pocket - Vue des cartes et statistiques"
                  className="relative w-full h-auto rounded-xl shadow-xl"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Comment Ça Marche - Design amélioré */}
      <section aria-labelledby="how-it-works-heading" className="bg-gray-50 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full mb-3 inline-block">{t('landing.how_it_works.label')}</span>
            <h2 id="how-it-works-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('landing.how_it_works.title')}</h2>
            <p className="mt-2 max-w-2xl mx-auto text-lg text-gray-600">
              {t('landing.how_it_works.description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="absolute top-0 right-0 bg-green-100 text-green-700 font-bold px-4 py-1 text-sm rounded-bl-lg">{t('landing.how_it_works.step1')}</div>
              <div className="flex justify-center mb-6">
                <div className="bg-green-100 p-4 rounded-full">
                  <Download className="w-10 h-10 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-4">{t('landing.how_it_works.step1_title')}</h3>
              <p className="text-gray-600 text-center">
                {t('landing.how_it_works.step1_text')}
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="absolute top-0 right-0 bg-blue-100 text-blue-700 font-bold px-4 py-1 text-sm rounded-bl-lg">{t('landing.how_it_works.step2')}</div>
              <div className="flex justify-center mb-6">
                <div className="bg-blue-100 p-4 rounded-full">
                  <LayoutGrid className="w-10 h-10 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-4">{t('landing.how_it_works.step2_title')}</h3>
              <p className="text-gray-600 text-center">
                {t('landing.how_it_works.step2_text')}
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="absolute top-0 right-0 bg-purple-100 text-purple-700 font-bold px-4 py-1 text-sm rounded-bl-lg">{t('landing.how_it_works.step3')}</div>
              <div className="flex justify-center mb-6">
                <div className="bg-purple-100 p-4 rounded-full">
                  <Share2 className="w-10 h-10 text-purple-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-4">{t('landing.how_it_works.step3_title')}</h3>
              <p className="text-gray-600 text-center">
                {t('landing.how_it_works.step3_text')}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Section FAQ - Design amélioré */}
      <section aria-labelledby="faq-heading" className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="bg-amber-100 text-amber-800 text-xs font-medium px-3 py-1 rounded-full mb-3 inline-block">{t('landing.faq.label')}</span>
            <h2 id="faq-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{t('landing.faq.title')}</h2>
            <p className="mt-2 max-w-2xl mx-auto text-lg text-gray-600">
              {t('landing.faq.description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-amber-100 rounded-full p-3">
                  <HelpCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('landing.faq.q1')}</h3>
                  <p className="text-gray-600">
                    {t('landing.faq.a1')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-amber-100 rounded-full p-3">
                  <HelpCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('landing.faq.q2')}</h3>
                  <p className="text-gray-600">
                    {t('landing.faq.a2')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-amber-100 rounded-full p-3">
                  <HelpCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('landing.faq.q3')}</h3>
                  <p className="text-gray-600">
                    {t('landing.faq.a3')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-amber-100 rounded-full p-3">
                  <HelpCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('landing.faq.q4')}</h3>
                  <p className="text-gray-600">
                    {t('landing.faq.a4')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </section>
      
      {/* Section CTA - Design amélioré */}
      <section aria-labelledby="cta-heading" className="hero-gradient py-16 sm:py-20 relative overflow-hidden">
        <div className="pokeball-bg absolute top-20 left-[15%] opacity-30"></div>
        <div className="pokeball-bg absolute bottom-20 right-[15%] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-3xl mx-auto bg-white/10 backdrop-blur-md p-8 sm:p-12 rounded-2xl border border-white/20">
            <h2 id="cta-heading" className="text-3xl sm:text-4xl font-bold text-white mb-6 drop-shadow-sm">
              {t('landing.cta.title')}
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {t('landing.cta.description')}
            </p>
            <Link
              to={getPathForLang("/register")}
              className="inline-flex items-center bg-white text-blue-600 hover:text-blue-700 text-lg px-8 py-4 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
            >
              {t('landing.cta.button')}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}